import { useGetLiveUpdatesSummaryQuery } from 'api/liveUpdates'
import type { LiveUpdatesSummary } from 'api/liveUpdates.types'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useFlag from 'hooks/useFlag'
import { useRbacNavDisabledWithReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { useEffect, useRef } from 'react'

const t = buildTranslate('nav.experiences_menu')

export type UseLiveUpdatesAccess = {
  // The `live-updates-merchant-config` LD flag is on for this merchant.
  enabled: boolean
  // The merchant is eligible per the summary endpoint (FR-76).
  eligible: boolean
  // The summary payload (warnings, flybuy, etc.) once loaded.
  summary: LiveUpdatesSummary | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  // Show the nav link / page at all: the flag is on. An ineligible merchant
  // (AI-720: no ordering) still renders the link, greyed out via `disabled`,
  // rather than disappearing — the nav should show this is something they
  // could unlock by enabling ordering, not hide it as if it didn't exist.
  show: boolean
  // True when the merchant is ineligible (no ordering, FR-76) or the user
  // lacks the MerchantSettings permission the feature maps to. The RBAC half
  // is withheld while `identityLoading` is true, so the nav item doesn't
  // flash disabled with a misleading permission reason on a hard refresh.
  disabled: boolean
  disabledReason: string | undefined
  // RBAC only — the user holds the MerchantSettings permission the feature
  // maps to. Deliberately independent of `eligible`: the summary query is now
  // skipped for an unpermitted user, so `eligible` never resolves for them —
  // the scene checks this signal directly (a bare `!access.canAccess`) and
  // evaluates the FR-76 eligibility redirect BEFORE it, rather than composing
  // the two into one expression.
  canAccess: boolean
  // True until the Merchant + MerchantUser ORM records backing `ability` have
  // loaded. On a hard refresh those load asynchronously while `canAccess` is
  // computed synchronously off whatever `ability` currently holds, which
  // defaults to "deny everything" — so a caller must not trust `canAccess`
  // (or redirect on it) while this is true.
  identityLoading: boolean
}

// Single source of truth for "can this merchant + user use Live Updates",
// composed from the LD flag, the summary eligibility check (FR-76), and the
// MerchantSettings RBAC gate. Consumed by BOTH the Digital experiences nav item
// and the scene so the gate cannot drift between them (mirrors useOrderingAccess,
// which exists so ordering links gate consistently across nav and scenes).
//
// The LD flag only ever controls the nav item's visibility (`show`) — it is
// intentionally NOT a gate on scene access. A merchant/user can navigate to
// /live_updates directly (e.g. a bookmarked or shared link) while the flag is
// off; eligibility (FR-76) and RBAC still apply regardless. The scene passes
// `requireFlag: false` so its summary fetch (and therefore `eligible`) doesn't
// depend on the flag either; the nav omits the option (defaults to requiring
// the flag) so it doesn't fire the summary request for merchants outside the
// flag's rollout.
export function useLiveUpdatesAccess(
  options: { requireFlag?: boolean } = {}
): UseLiveUpdatesAccess {
  const { requireFlag = true } = options
  const enabled = useFlag('live-updates-merchant-config', false)
  // No dedicated CASL subject exists yet for Live Updates — 'MerchantSettings'
  // ("Dashboard Management" in permissionMap.ts) is the closest mapping and
  // matches how Settings gates its own merchant-config links.
  //
  // Declared ABOVE the summary query because it now gates it (see `skip`), not
  // only the nav item's appearance.
  const { disabled: rbacDisabled, disabledReason: rbacReason } =
    useRbacNavDisabledWithReason('MerchantSettings')

  // `ability` (and therefore `rbacDisabled`) defaults to "deny everything"
  // until the Merchant + MerchantUser ORM records it's derived from have
  // loaded (see utilities/ability.tsx). Those load asynchronously on a hard
  // refresh, independently of this hook's own summary fetch, so a caller
  // must not trust `canAccess` until both have arrived.
  //
  // Unlike ProtectedRoute.tsx, this hook has no explicit legalDocsEnforced
  // bypass for the case where those records never resolve (a legal-docs-
  // blocked admin's identity reads 403 forever) — a caller stuck with
  // `identityLoading: true` relies on LegalDocsNagGuard (mounted app-wide in
  // AppLayout, blocking navigation to any route but the terms page) to force
  // the user away before the missing bypass would matter. It isn't specific
  // to /live_updates — it's a general denylist that happens to cover this
  // route along with every other one. If that mechanism is ever narrowed to
  // an allowlist that excludes /live_updates, this hook would need its own
  // bypass to avoid an indefinite loading state for that case.
  const merchant = useCurrentMerchant()
  const merchantUser = useCurrentMerchantUser()
  const identityLoading = !merchant || !merchantUser

  // GET live_updates/summary is gated server-side on
  // `rbac_authorize!('view', 'merchant_settings')`, so calling it without that
  // permission can only 403 — and utilities/api.ts + api/index.ts surface every
  // RBAC 403 as a page-level danger banner. This hook mounts eagerly in the
  // Digital experiences nav on EVERY authenticated page (Bar/MenuItem renders
  // its dropdown children while closed), so an unguarded call put that banner in
  // front of every user without the permission, on every page, for a request
  // they never asked for. The LD flag is a ROLLOUT gate and was never a stand-in
  // for this: it stopped masking the problem the moment the flag reached 100%.
  //
  // Gated on the RAW `rbacDisabled`, deliberately NOT `rbacGateActive` below:
  // during the identity-loading window `ability` denies everything, so raw
  // rbacDisabled reads true and the request is withheld until identity settles
  // — at which point it either fires (permitted) or stays skipped forever
  // (not permitted). Keying off `rbacGateActive` (false while identityLoading)
  // would instead let exactly one 403 through per hard refresh, which is the
  // whole bug.
  const summaryQuery = useGetLiveUpdatesSummaryQuery(undefined, {
    skip: (requireFlag && !enabled) || rbacDisabled,
  })

  // A transient summary failure would otherwise hide the feature for the whole
  // session (eligibility never resolves, so the nav item and page both vanish).
  // Retry once on error so a single blip self-heals; a persistent failure still
  // fails closed (the feature stays hidden). setupListeners isn't wired app-wide,
  // so refetchOnFocus/Reconnect aren't available here.
  //
  // The re-arm check must key off `isSuccess && !isFetching`, not `!isError`
  // or `isSuccess` alone: calling `refetch()` moves the query to `pending`,
  // where `isError` is false too — keying off `!isError` would re-arm
  // `retriedRef` during the retry's own in-flight window. `isSuccess` alone
  // isn't safe either: RTK Query derives the hook-level `isSuccess` as
  // `currentState.isSuccess || (isFetching && hasData)`, so a retry's pending
  // window ALSO reads isSuccess true whenever the cache still holds data from
  // an earlier successful load (e.g. a persistent failure that started after
  // the summary had already loaded once) — re-arming the guard mid-retry and
  // reopening the unbounded-loop window. Requiring `!isFetching` means the
  // guard only resets once the query has actually SETTLED on success, not
  // merely while in flight with stale cached data behind it.
  //
  // 4xx is excluded: a client error is a verdict, not a blip. Retrying a 403
  // buys a second guaranteed failure and a SECOND danger banner, which is how
  // one unguarded probe became two toasts per page load. The skip above should
  // now prevent that 403 from happening at all; this stays as defence in depth
  // for the cases skip can't cover (e.g. a permission revoked mid-session, or
  // the endpoint 404ing on a merchant).
  const { isError, isSuccess, isFetching, refetch, error } = summaryQuery
  const isClientError =
    !!error &&
    'status' in error &&
    typeof error.status === 'number' &&
    error.status >= 400 &&
    error.status < 500
  const retriedRef = useRef(false)
  useEffect(() => {
    if (isError && !isClientError && !retriedRef.current) {
      retriedRef.current = true
      refetch()
    } else if (isSuccess && !isFetching) {
      retriedRef.current = false
    }
  }, [isError, isClientError, isSuccess, isFetching, refetch])

  const eligible = summaryQuery.data?.eligible === true
  // Distinct from `!eligible`: this is only true once the summary has
  // actually resolved and reported ineligibility, not merely because it
  // hasn't loaded yet (or a persistent error left `data` undefined). Without
  // this split, a loading/erroring merchant would render the link disabled
  // with "Requires \"ordering\" to be enabled" — wrong, and it would also violate
  // the hook's fail-closed contract above (a persistent failure should hide
  // the feature, not show it greyed out for the wrong reason).
  const confirmedIneligible = summaryQuery.data?.eligible === false
  // `eligible || confirmedIneligible` is exactly "the summary resolved to a
  // known eligibility value" — spelled out directly rather than left as a
  // tautology callers have to reverse-engineer.
  const eligibilityResolved = summaryQuery.data !== undefined

  // AI-720: a confirmed-ineligible merchant (no ordering) greys the nav item
  // out instead of hiding it, with a reason explaining why — RBAC still
  // applies on top for an eligible merchant whose user lacks the permission.
  // The RBAC half is withheld while identityLoading is true (see above) so
  // the nav doesn't flash "requires permission" for an authorized user on a
  // hard refresh — confirmedIneligible is unaffected since it comes from the
  // summary fetch, not the ability.
  const rbacGateActive = rbacDisabled && !identityLoading
  const disabled = confirmedIneligible || rbacGateActive
  let disabledReason: string | undefined
  if (confirmedIneligible) {
    disabledReason = t('live_updates_ordering_required')
  } else if (rbacGateActive) {
    disabledReason = rbacReason
  } else {
    disabledReason = undefined
  }

  return {
    enabled,
    eligible,
    summary: summaryQuery.data,
    isLoading: summaryQuery.isLoading,
    isError: summaryQuery.isError,
    refetch: summaryQuery.refetch,
    // `|| rbacGateActive`: the summary request is skipped for an unpermitted
    // user, so `eligibilityResolved` never becomes true for them. Without this
    // term the nav link would DISAPPEAR for that user instead of rendering
    // greyed out with the permission reason — regressing AI-720, which chose
    // visible-but-disabled over hidden.
    show: enabled && (eligibilityResolved || rbacGateActive),
    disabled,
    disabledReason,
    canAccess: !rbacDisabled,
    identityLoading,
  }
}

export default useLiveUpdatesAccess
